import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { EChartsOption } from 'echarts';
import DataExplorer from './DataExplorer';
import TemplateLibrary from './TemplateLibrary';
import CfoDashboard from './CfoDashboard';
import CfoProjects from './CfoProjects';
import { buildTemplateCatalog } from './templateCatalog';
import {
  buildChartCatalog,
  dashboards,
  downloadCsv,
  formatCell,
  fullMoney,
  initChart,
  money,
  num,
  pct,
  type ChartDefinition,
  type DashboardData,
  type DrillSelection,
  type Filters,
  type Row,
  type User,
} from './analytics';

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...init?.headers }, ...init });
  if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || `HTTP ${response.status}`);
  return response.status === 204 ? (undefined as T) : response.json();
}

type ChartProps = {
  definition: ChartDefinition;
  className?: string;
  compact?: boolean;
  onOpen: (selection?: DrillSelection) => void;
};

function Chart({ definition, className = '', compact = false, onOpen }: ChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onOpenRef = useRef(onOpen);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  useEffect(() => {
    if (!ref.current) return;
    const instance = initChart(ref.current);
    instance.setOption(definition.option as EChartsOption);
    const click = (event: unknown) => {
      const value = event as { data?: { drill?: DrillSelection }; dataType?: string };
      onOpenRef.current(value.data?.drill);
    };
    instance.on('click', click);
    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(ref.current);
    return () => { observer.disconnect(); instance.off('click', click); instance.dispose(); };
  }, [definition]);
  return <div
    ref={ref}
    className={`chart ${compact ? 'chart-compact' : ''} ${className}`}
    role="button"
    tabIndex={0}
    aria-label={`${definition.title}. Premi Invio o seleziona un elemento per aprire i dettagli.`}
    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(); } }}
  />;
}

function Kpi({ label, value, delta, tone = 'default', onClick }: { label: string; value: string; delta?: string; tone?: string; onClick?: () => void }) {
  return <button className={`kpi ${tone}`} onClick={onClick}>
    <span>{label}</span><strong>{value}</strong>{delta && <small>{delta}</small>}<i /><b>Esplora →</b>
  </button>;
}

function ChartPanel({ definition, onOpen, index }: { definition: ChartDefinition; onOpen: (selection?: DrillSelection) => void; index: number }) {
  const wide = definition.height === 'tall' || index === 0;
  return <article className={`panel chart-panel ${wide ? 'wide' : ''}`} data-chart-id={definition.id}>
    <div className="panel-head">
      <div><span className="eyebrow">{definition.kicker}</span><h2>{definition.title}</h2><p>{definition.description}</p></div>
      <div className="panel-tools"><span>{definition.family}</span><button onClick={() => onOpen()} aria-label={`Apri dettagli di ${definition.title}`}>↗</button></div>
    </div>
    <Chart definition={definition} onOpen={onOpen} />
    <button className="click-hint" onClick={() => onOpen()}><i /> Clicca un dato per esplorare · Apri analisi completa</button>
  </article>;
}

const selectionKeys: Record<DrillSelection['dimension'], string[]> = {
  month: ['month', 'issue_date'],
  project: ['project_id', 'project_name'],
  category: ['category'],
  supplier: ['supplier_id', 'supplier_name'],
  document: ['id', 'document_number'],
  metric: ['metric'],
};

function contextualRows(definition: ChartDefinition, selection?: DrillSelection) {
  if (!selection) return definition.rows;
  const keys = selectionKeys[selection.dimension];
  const matching = definition.rows.filter((row) => keys.some((key) => {
    const value = String(row[key] ?? '');
    return value === selection.key || value === selection.label || value.startsWith(selection.key);
  }));
  return matching.length ? matching : definition.rows;
}

type DrawerProps = {
  catalog: Record<string, ChartDefinition>;
  stack: string[];
  selection?: DrillSelection;
  onClose: () => void;
  onBack: () => void;
  onJump: (index: number) => void;
  onNavigate: (id: string, selection?: DrillSelection) => void;
};

function DetailDrawer({ catalog, stack, selection, onClose, onBack, onJump, onNavigate }: DrawerProps) {
  const definition = catalog[stack.at(-1) || ''];
  const [tab, setTab] = useState<'summary' | 'explore' | 'table' | 'related'>('summary');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [ascending, setAscending] = useState(false);
  const [limit, setLimit] = useState(25);
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setTab('summary'); setQuery(''); setSortKey(''); setLimit(25);
    window.setTimeout(() => drawerRef.current?.focus(), 50);
  }, [definition?.id]);
  useEffect(() => {
    document.body.classList.add('drawer-open');
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => { document.body.classList.remove('drawer-open'); window.removeEventListener('keydown', closeOnEscape); };
  }, [onClose]);

  const filteredRows = useMemo(() => {
    if (!definition) return [];
    let rows = contextualRows(definition, selection);
    if (query.trim()) {
      const normalized = query.toLocaleLowerCase('it');
      rows = rows.filter((row) => Object.values(row).some((value) => String(value ?? '').toLocaleLowerCase('it').includes(normalized)));
    }
    if (sortKey) rows = [...rows].sort((left, right) => {
      const a = left[sortKey]; const b = right[sortKey];
      const result = typeof a === 'number' || typeof b === 'number' ? num(a) - num(b) : String(a ?? '').localeCompare(String(b ?? ''), 'it');
      return ascending ? result : -result;
    });
    return rows;
  }, [definition, selection, query, sortKey, ascending]);

  if (!definition) return null;
  const ids = Object.keys(catalog);
  const activeIndex = ids.indexOf(definition.id);
  const previous = ids[(activeIndex - 1 + ids.length) % ids.length];
  const next = ids[(activeIndex + 1) % ids.length];
  const tabs = [
    ['summary', 'Sintesi'], ['explore', 'Esplora'], ['table', 'Tabella'], ['related', 'Correlati'],
  ] as const;

  return <div className="drawer-layer" aria-label="Dettagli aperti">
    <button className="drawer-scrim" onClick={onClose} aria-label="Chiudi pannello" />
    <aside className="detail-drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title" tabIndex={-1} ref={drawerRef}>
      <header className="drawer-header">
        <nav aria-label="Percorso pannelli" className="drawer-breadcrumbs">
          {stack.map((id, index) => <React.Fragment key={`${id}-${index}`}><button onClick={() => onJump(index)}>{catalog[id]?.title}</button>{index < stack.length - 1 && <span>›</span>}</React.Fragment>)}
        </nav>
        <div className="drawer-title-row">
          <div><span className="eyebrow">{definition.kicker} · {definition.family}</span><h2 id="drawer-title">{definition.title}</h2><p>{selection ? `Focus: ${selection.label}` : definition.description}</p></div>
          <button className="drawer-close" onClick={onClose} aria-label="Chiudi tutti i pannelli">×</button>
        </div>
        <div className="drawer-nav-controls">
          <button onClick={() => onNavigate(previous)}>← Analisi precedente</button>
          {stack.length > 1 && <button onClick={onBack}>Torna indietro</button>}
          <button onClick={() => onNavigate(next)}>Analisi successiva →</button>
        </div>
      </header>
      <nav className="drawer-tabs" aria-label="Sezioni del pannello">
        {tabs.map(([id, label]) => <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}{id === 'table' && <span>{filteredRows.length}</span>}</button>)}
      </nav>
      <div className="drawer-body">
        {tab === 'summary' && <section className="drawer-section">
          {selection && <div className="focus-card"><span>SELEZIONE ATTIVA</span><strong>{selection.label}</strong><small>{selection.dimension} · {selection.key}</small><button onClick={() => onNavigate(definition.id)}>Rimuovi focus</button></div>}
          <div className="insight-grid">{definition.insights.map((item) => <article key={item.label} className={item.tone || ''}><span>{item.label}</span><strong>{item.value}</strong><i /></article>)}</div>
          <div className="narrative-card"><span className="eyebrow">COME LEGGERE IL GRAFICO</span><h3>{definition.family}</h3><p>{definition.description}</p><ul><li>Seleziona un punto, una barra o un nodo per contestualizzare il dettaglio.</li><li>Usa “Esplora” per ingrandire e navigare la visualizzazione.</li><li>Apri “Tabella” per ricercare, ordinare ed esportare i dati sottostanti.</li></ul></div>
          <button className="drawer-primary" onClick={() => setTab('explore')}>Esplora la visualizzazione <span>→</span></button>
        </section>}
        {tab === 'explore' && <section className="drawer-section explore-section">
          <div className="explore-toolbar"><div><span>Modalità interattiva</span><strong>Zoom · tooltip · selezione</strong></div><button onClick={() => setTab('table')}>Apri dati tabellari</button></div>
          <div className="drawer-chart"><Chart definition={definition} onOpen={(nextSelection) => onNavigate(definition.id, nextSelection)} /></div>
          <div className="explore-foot"><span><i /> Animazioni ECharts attive</span><span>{definition.rows.length} record sottostanti</span></div>
        </section>}
        {tab === 'table' && <section className="drawer-section table-section">
          <div className="table-controls">
            <label><span>Cerca nei dati</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Commessa, fornitore, stato…" /></label>
            <label><span>Righe</span><select value={limit} onChange={(event) => setLimit(Number(event.target.value))}><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></select></label>
            <button onClick={() => downloadCsv(definition, filteredRows)}>Esporta CSV ↓</button>
          </div>
          <div className="drawer-table-wrap"><table><thead><tr>{definition.columns.map((column) => <th key={column.key}><button onClick={() => { if (sortKey === column.key) setAscending(!ascending); else { setSortKey(column.key); setAscending(false); } }}>{column.label}{sortKey === column.key ? (ascending ? ' ↑' : ' ↓') : ''}</button></th>)}</tr></thead><tbody>{filteredRows.slice(0, limit).map((row, index) => <tr key={`${String(row.id ?? row.project_id ?? row.supplier_id ?? index)}-${index}`}>{definition.columns.map((column) => <td key={column.key}>{formatCell(row[column.key], column.format)}</td>)}</tr>)}</tbody></table></div>
          {!filteredRows.length && <div className="empty-state">Nessun record corrisponde ai controlli selezionati.</div>}
          <div className="table-meta">Mostrate {Math.min(limit, filteredRows.length)} di {filteredRows.length} righe</div>
        </section>}
        {tab === 'related' && <section className="drawer-section related-section">
          <div className="section-intro"><span className="eyebrow">NAVIGAZIONE DINAMICA</span><h3>Continua l’analisi senza perdere il contesto</h3><p>Ogni apertura aggiunge un livello al percorso in alto. Puoi tornare a qualsiasi passaggio.</p></div>
          <div className="related-grid">{definition.related.map((id) => {
            const related = catalog[id]; if (!related) return null;
            return <button key={id} onClick={() => onNavigate(id)}><span>{related.family}</span><strong>{related.title}</strong><small>{related.description}</small><b>Apri analisi →</b></button>;
          })}</div>
        </section>}
      </div>
      <footer className="drawer-footer"><span>I filtri globali restano attivi in tutto il percorso.</span><button onClick={onClose}>Chiudi</button></footer>
    </aside>
  </div>;
}

function DocumentArchive({ data, onOpen }: { data: DashboardData; onOpen: (selection?: DrillSelection) => void }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const rows = data.documents.filter((row) => (!status || row.status === status) && (!query || Object.values(row).some((value) => String(value ?? '').toLocaleLowerCase('it').includes(query.toLocaleLowerCase('it')))));
  const statuses = [...new Set(data.documents.map((row) => String(row.status)))];
  return <section className="archive-panel">
    <div className="archive-head"><div><span className="eyebrow">DRILL-DOWN OPERATIVO</span><h2>Documenti e movimenti</h2><p>Archivio filtrabile collegato alle dashboard economiche e finanziarie.</p></div><button onClick={() => onOpen()}>Apri timeline ↗</button></div>
    <div className="archive-controls"><label>Cerca<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Numero, controparte, commessa…" /></label><label>Stato<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">Tutti</option>{statuses.map((item) => <option key={item}>{item}</option>)}</select></label><span>{rows.length} record</span></div>
    <div className="table-wrap"><table><thead><tr><th>Documento</th><th>Data</th><th>Tipo</th><th>Controparte</th><th>Commessa</th><th>Stato</th><th className="right">Imponibile</th></tr></thead><tbody>{rows.map((row) => <tr key={String(row.id)} tabIndex={0} onClick={() => onOpen({ dimension: 'document', key: String(row.id), label: String(row.document_number) })} onKeyDown={(event) => { if (event.key === 'Enter') onOpen({ dimension: 'document', key: String(row.id), label: String(row.document_number) }); }}><td><b>{row.document_number}</b></td><td>{new Date(String(row.issue_date)).toLocaleDateString('it-IT')}</td><td>{row.kind === 'SALES' ? 'Vendita' : 'Acquisto'}</td><td>{row.counterparty}</td><td>{row.project_name || '—'}</td><td><span className={`status ${String(row.status).toLowerCase()}`}>{row.status}</span></td><td className="right">{fullMoney.format(num(row.total_net))}</td></tr>)}</tbody></table></div>
  </section>;
}

export default function Dashboard({ user, onLogout }: { user: User; onLogout: () => void }) {
  const initialPage = window.location.hash.match(/#\/dashboard\/([a-z-]+)/)?.[1];
  const [filters, setFilters] = useState<Filters | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [company, setCompany] = useState(user.company || '');
  const [project, setProject] = useState('');
  const [from, setFrom] = useState('2024-09-01');
  const [to, setTo] = useState('2026-08-31');
  const specialPages = ['edilfast-cfo', 'templates', 'data-explorer', 'documents'];
  const defaultPage = user.company === 'EDILFAST' ? 'edilfast-cfo' : 'overview';
  const [page, setPage] = useState(specialPages.includes(initialPage || '') || dashboards.some((item) => item.id === initialPage) ? initialPage || defaultPage : defaultPage);
  const [dark, setDark] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerStack, setDrawerStack] = useState<string[]>([]);
  const [drawerSelection, setDrawerSelection] = useState<DrillSelection | undefined>();

  useEffect(() => { api<Filters>('/api/filters').then((value) => { setFilters(value); setFrom(value.range.from); setTo(value.range.to); }).catch((reason) => setError(reason.message)); }, []);
  useEffect(() => {
    setLoading(true); setError('');
    const query = new URLSearchParams({ from, to }); if (company) query.set('company', company); if (project) query.set('project', project);
    api<DashboardData>(`/api/dashboard?${query}`).then(setData).catch((reason) => setError(reason.message)).finally(() => setLoading(false));
  }, [company, project, from, to]);
  useEffect(() => { document.documentElement.dataset.theme = dark ? 'dark' : 'light'; }, [dark]);
  useEffect(() => { window.history.replaceState({}, '', `#/dashboard/${page}`); setDrawerStack([]); setDrawerSelection(undefined); }, [page]);

  const dashboardCatalog = useMemo(() => data ? buildChartCatalog(data) : {}, [data]);
  const templates = useMemo(() => data ? buildTemplateCatalog(data) : [], [data]);
  const catalog = useMemo(() => ({ ...dashboardCatalog, ...Object.fromEntries(templates.map((template) => [template.id, template])) }), [dashboardCatalog, templates]);
  const currentDashboard = dashboards.find((item) => item.id === page) || dashboards[0];
  const isDashboardPage = dashboards.some((item) => item.id === page);
  const cfoAccess = user.role === 'ADMIN' || user.company === 'EDILFAST';
  const isCfoProjectPage = cfoAccess && page === 'projects';
  const pageMeta = page === 'edilfast-cfo'
    ? { group: 'EDILFAST', title: 'Controllo di gestione', subtitle: 'Margini di commessa, costi allocati, impegni residui e qualità del dato.' }
    : isCfoProjectPage
      ? { group: 'EDILFAST', title: 'Commesse', subtitle: 'Ciclo di vita completo, fatturazione, costi, impegni, personale e documenti.' }
    : page === 'templates'
    ? { group: 'LABORATORIO', title: 'Template Library', subtitle: '100 modelli grafici professionali, interattivi e riutilizzabili.' }
    : page === 'data-explorer'
      ? { group: 'DATI', title: 'Data Explorer', subtitle: 'Entità, record e relazioni del database in una vista visuale e tabellare.' }
      : page === 'documents'
        ? { group: 'DATI', title: 'Documenti e movimenti', subtitle: 'Dal dato aggregato al singolo documento, senza perdere il contesto.' }
        : { group: currentDashboard.group.toUpperCase(), title: currentDashboard.title, subtitle: currentDashboard.subtitle };
  const selectedCompanyId = filters?.companies.find((item) => item.code === company)?.id;
  const visibleProjects = filters?.projects.filter((item) => !selectedCompanyId || item.company_id === selectedCompanyId) || [];
  const latestCash = num(data?.cash.at(-1)?.cash);
  const openDrawer = (id: string, selection?: DrillSelection) => {
    setDrawerSelection(selection);
    setDrawerStack((current) => current.at(-1) === id ? current : [...current, id]);
  };
  const logout = async () => { await api('/api/logout', { method: 'POST' }); onLogout(); };
  const navGroups = ['Direzione', 'Analisi', 'Laboratorio'] as const;
  const dataEntityCount = cfoAccess ? 23 : 14;

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><span className="brand-mark">M</span><span>MEINARDI <b>DEV</b></span></div>
      <nav aria-label="Dashboard analytics">
        {cfoAccess && <section><p>Edilfast</p><button className={page === 'edilfast-cfo' ? 'active' : ''} onClick={() => setPage('edilfast-cfo')}><span>◫</span><b>Controllo gestione</b></button></section>}
        {navGroups.map((group) => <section key={group}><p>{group}</p>{dashboards.filter((item) => item.group === group).map((item) => <button key={item.id} className={page === item.id ? 'active' : ''} onClick={() => setPage(item.id)}><span>{item.icon}</span><b>{item.shortTitle}</b>{item.id === 'risk' && data && data.projects.filter((row) => num(row.margin_pct) < 15).length > 0 && <i>{data.projects.filter((row) => num(row.margin_pct) < 15).length}</i>}</button>)}{group === 'Laboratorio' && <button className={page === 'templates' ? 'active' : ''} onClick={() => setPage('templates')}><span>◇</span><b>100 Template</b><i>100</i></button>}</section>)}
        <section><p>Dati</p><button className={page === 'data-explorer' ? 'active' : ''} onClick={() => setPage('data-explorer')}><span>⌘</span><b>Data Explorer</b><i>{dataEntityCount}</i></button><button className={page === 'documents' ? 'active' : ''} onClick={() => setPage('documents')}><span>≡</span><b>Documenti</b>{data && <i>{data.documents.length}</i>}</button></section>
      </nav>
      <div className="sidebar-foot"><div className="user-avatar">{user.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}</div><div><strong>{user.name}</strong><small>{user.role === 'ADMIN' ? 'Accesso completo' : user.company}</small></div><button onClick={logout} title="Esci">↗</button></div>
    </aside>
    <main className="workspace">
      <header className="topbar">
        <div><span className="eyebrow">CFO · ANALYTICS LAB / {pageMeta.group}</span><h1>{pageMeta.title}</h1><p>{pageMeta.subtitle}</p></div>
        <div className="top-actions"><span className="lab-count"><strong>{page === 'edilfast-cfo' ? '3' : isCfoProjectPage ? '5' : '10'}</strong> {page === 'edilfast-cfo' ? 'livelli di margine' : isCfoProjectPage ? 'viste commessa' : 'dashboard'} · <strong>{page === 'edilfast-cfo' || isCfoProjectPage ? '2' : '100'}</strong> scenari</span><button className="theme" onClick={() => setDark(!dark)} aria-label="Cambia tema">{dark ? '☼' : '☾'}</button><span className="live"><i /> {page === 'edilfast-cfo' || isCfoProjectPage ? 'Dati gestionali' : 'Dati demo live'}</span></div>
      </header>
      {page !== 'data-explorer' && page !== 'edilfast-cfo' && !isCfoProjectPage && <section className="filterbar">
        {user.role === 'ADMIN' && <label>Azienda<select value={company} onChange={(event) => { setCompany(event.target.value); setProject(''); }}><option value="">Tutte le aziende</option>{filters?.companies.map((item) => <option key={item.id} value={item.code}>{item.name}</option>)}</select></label>}
        <label>Da<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>A<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <label>Commessa<select value={project} onChange={(event) => setProject(event.target.value)}><option value="">Tutte le commesse</option>{visibleProjects.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <button className="reset" onClick={() => { setCompany(user.company || ''); setProject(''); setFrom(filters?.range.from || '2024-09-01'); setTo(filters?.range.to || '2026-08-31'); }}>Azzera filtri</button>
      </section>}
      {page === 'data-explorer' && <section className="filterbar explorer-filterbar">
        {user.role === 'ADMIN' && <label>Azienda<select value={company} onChange={(event) => setCompany(event.target.value)}><option value="">Tutte le aziende</option>{filters?.companies.map((item) => <option key={item.id} value={item.code}>{item.name}</option>)}</select></label>}
        <span><i /> Perimetro: {company || 'tutte le aziende autorizzate'}</span><b>API governata · query allowlisted</b>
      </section>}
      {error && page !== 'edilfast-cfo' && !isCfoProjectPage && <div className="banner error">{error}</div>}
      {loading && page !== 'edilfast-cfo' && !isCfoProjectPage && <div className="loading"><i /><span>Allineamento metriche in corso…</span></div>}
      {page === 'edilfast-cfo' && <CfoDashboard />}
      {isCfoProjectPage && <CfoProjects />}
      {!loading && data && isDashboardPage && !isCfoProjectPage && <>
        <section className="kpi-grid">
          <Kpi label="Ricavi" value={money.format(num(data.kpis.revenue))} delta={`${num(data.kpis.budget_variance) >= 0 ? '+' : ''}${money.format(num(data.kpis.budget_variance))} vs budget`} onClick={() => openDrawer('revenue-trend')} />
          <Kpi label="Margine" value={money.format(num(data.kpis.industrial_margin))} onClick={() => openDrawer('margin-gauge')} />
          <Kpi label="Margine %" value={`${pct.format(num(data.kpis.margin_pct))}%`} tone={num(data.kpis.margin_pct) < 20 ? 'danger' : 'good'} onClick={() => openDrawer('margin-gauge')} />
          <Kpi label="EBITDA" value={money.format(num(data.kpis.ebitda))} tone={num(data.kpis.ebitda) < 0 ? 'danger' : 'good'} onClick={() => openDrawer('ebitda-waterfall')} />
          <Kpi label="Cash" value={money.format(latestCash)} onClick={() => openDrawer('cash-flow')} />
          <Kpi label="Commesse" value={String(data.projects.length)} delta={`${data.projects.filter((row) => num(row.margin_pct) < 15).length} critiche`} tone={data.projects.some((row) => num(row.margin_pct) < 15) ? 'danger' : 'good'} onClick={() => openDrawer('project-bubble')} />
        </section>
        <div className="dashboard-context"><span><i /> Dashboard {dashboards.findIndex((item) => item.id === page) + 1} di {dashboards.length}</span><p>Clicca qualsiasi elemento grafico per aprire il pannello di analisi a destra.</p></div>
        <section className="chart-grid">{currentDashboard.charts.map((id, index) => catalog[id] && <ChartPanel key={id} definition={catalog[id]} index={index} onOpen={(selection) => openDrawer(id, selection)} />)}</section>
      </>}
      {!loading && data && page === 'templates' && <TemplateLibrary templates={templates} onOpen={openDrawer} />}
      {!loading && page === 'data-explorer' && <DataExplorer company={company} canEdit={user.role === 'ADMIN'} />}
      {!loading && data && page === 'documents' && <DocumentArchive data={data} onOpen={(selection) => openDrawer('document-timeline', selection)} />}
    </main>
    {drawerStack.length > 0 && <DetailDrawer
      catalog={catalog}
      stack={drawerStack}
      selection={drawerSelection}
      onClose={() => { setDrawerStack([]); setDrawerSelection(undefined); }}
      onBack={() => { setDrawerStack((current) => current.slice(0, -1)); setDrawerSelection(undefined); }}
      onJump={(index) => { setDrawerStack((current) => current.slice(0, index + 1)); setDrawerSelection(undefined); }}
      onNavigate={openDrawer}
    />}
  </div>;
}
